/**
 * Patient photo preparation before upload. The API takes PNG or JPEG up to 2 MiB (RequestPatientPhotoUploadRequest);
 * camera photos are often larger. Every photo is re-encoded here as a JPEG no larger than PHOTO_MAX_SIDE on its longest
 * side and under the limit, lowering the quality and then the size until it fits. Re-encoding draws only the pixels, so
 * EXIF and other metadata (location, device, time) never leave the browser; the orientation is applied from the file
 * first. The file as picked is never sent: one the browser cannot decode or re-encode is refused. The server's limit is
 * unchanged.
 */

/** The server's limit (2 MiB). */
export const PHOTO_MAX_BYTES = 2_097_152;
/** Longest side of a re-encoded photo; plenty for recognising a patient at check-in. */
export const PHOTO_MAX_SIDE = 2048;
/** Smaller sides tried, in order, when a photo is still over the limit at the lowest quality. */
const FALLBACK_SIDES = [1600, 1280, 1024, 800];
const QUALITIES = [0.9, 0.8, 0.7, 0.6];

/** A decoded image, orientation applied. `encode` returns a JPEG at the given size and quality, or null if the browser could not. */
export interface DecodedImage {
  width: number;
  height: number;
  encode(width: number, height: number, quality: number): Promise<Blob | null>;
  close(): void;
}

export interface PhotoCodec {
  /** Null (or a rejection) when the browser cannot decode the file. */
  decode(file: Blob): Promise<DecodedImage | null>;
}

/** Always the browser's re-encode when ok: never the picked file's bytes. */
export type PreparedPhoto = { ok: true; blob: Blob; contentType: "image/jpeg"; width: number; height: number } | { ok: false; message: string };

const NOT_PREPARED_MESSAGE = "This photo couldn't be prepared for upload. Try another photo.";
const HEIC_MESSAGE = "This browser can't read HEIC photos. Use Safari, or save the photo as a JPEG first.";

function isHeic(file: Blob): boolean {
  const name = typeof (file as Partial<File>).name === "string" ? (file as File).name : "";
  return file.type === "image/heic" || file.type === "image/heif" || /\.hei[cf]$/i.test(name);
}

/** The refusal for a file the browser could not decode or re-encode; nothing of it is sent. */
function refused(file: Blob): PreparedPhoto {
  if (isHeic(file)) return { ok: false, message: HEIC_MESSAGE };
  if (file.type !== "image/png" && file.type !== "image/jpeg") return { ok: false, message: "Choose a PNG or JPEG image." };
  return { ok: false, message: NOT_PREPARED_MESSAGE };
}

/** The target sizes for an image whose longest side is `longest`: never upscaled. */
export function photoSides(longest: number): number[] {
  const first = Math.min(longest, PHOTO_MAX_SIDE);
  return [first, ...FALLBACK_SIDES.filter((s) => s < first)];
}

export async function preparePhoto(file: Blob, codec: PhotoCodec | null): Promise<PreparedPhoto> {
  const image = codec ? await codec.decode(file).catch(() => null) : null;
  if (!image || !(image.width > 0 && image.height > 0)) {
    image?.close();
    return refused(file);
  }
  try {
    const longest = Math.max(image.width, image.height);
    for (const side of photoSides(longest)) {
      const scale = side / longest;
      const width = Math.max(1, Math.round(image.width * scale));
      const height = Math.max(1, Math.round(image.height * scale));
      for (const quality of QUALITIES) {
        const blob = await image.encode(width, height, quality).catch(() => null);
        // The browser decoded but cannot encode: refuse rather than send the file as picked.
        if (!blob) return refused(file);
        if (blob.size <= PHOTO_MAX_BYTES) return { ok: true, blob, contentType: "image/jpeg", width, height };
      }
    }
    return { ok: false, message: "The photo is still over 2 MiB after reducing it. Choose a smaller image." };
  } finally {
    image.close();
  }
}

/**
 * The browser codec: createImageBitmap with the file's orientation applied, drawn on a canvas over white (a PNG's
 * transparency would otherwise turn black in a JPEG) and exported as JPEG. Null where createImageBitmap is missing.
 */
export function browserPhotoCodec(): PhotoCodec | null {
  if (typeof createImageBitmap === "undefined" || typeof document === "undefined") return null;
  return {
    async decode(file) {
      let bitmap: ImageBitmap;
      try {
        bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
      } catch {
        // Older engines reject the options bag; their default already follows the file's orientation or ignores it.
        bitmap = await createImageBitmap(file);
      }
      return {
        width: bitmap.width,
        height: bitmap.height,
        encode(width, height, quality) {
          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          if (!ctx) return Promise.resolve(null);
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, width, height);
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = "high";
          ctx.drawImage(bitmap, 0, 0, width, height);
          return new Promise<Blob | null>((resolve) =>
            canvas.toBlob(
              (blob) => {
                // Free the backing store now rather than at garbage collection: iPad Safari caps total canvas memory.
                canvas.width = 0;
                canvas.height = 0;
                resolve(blob && blob.type === "image/jpeg" ? blob : null);
              },
              "image/jpeg",
              quality,
            ),
          );
        },
        close: () => bitmap.close(),
      };
    },
  };
}
